import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { getService, createService, updateService, resolveImages } from '../../api';
import ImageUploader from '../../components/admin/ImageUploader';
import TagInput from '../../components/admin/TagInput';
import { useToast } from '../../components/Toast';
import Loader from '../../components/Loader';

const blank = { title: '', shortDescription: '', description: '', icon: '✨', tags: [], featured: false, order: 0, image: [] };

export default function ServiceForm() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const { toast, ToastHost } = useToast();
  const [form, setForm] = useState(blank);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!editing) return;
    getService(id)
      .then((s) => setForm({
        title: s.title, shortDescription: s.shortDescription || '', description: s.description || '',
        icon: s.icon || '✨', tags: s.tags || [], featured: !!s.featured, order: s.order || 0,
        image: s.image ? [s.image] : [],
      }))
      .catch(() => toast('Failed to load', 'error'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, editing]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast('Title is required', 'error');
    setSaving(true);
    try {
      const imgs = await resolveImages(form.image);
      const payload = {
        title: form.title, shortDescription: form.shortDescription, description: form.description,
        icon: form.icon, tags: form.tags, featured: form.featured, order: Number(form.order) || 0,
        image: imgs[0] || '',
      };
      if (editing) await updateService(id, payload);
      else await createService(payload);
      toast('Service saved');
      setTimeout(() => navigate('/admin/services'), 700);
    } catch (err) {
      toast(err.response?.data?.message || 'Save failed', 'error');
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <form onSubmit={submit}>
      <div className="acontent__head">
        <h1>{editing ? 'Edit' : 'New'} Service</h1>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => navigate('/admin/services')}><FiArrowLeft /> Back</button>
      </div>

      <div className="acard">
        <div className="form-grid">
          <div className="field">
            <label>Title <span className="req">*</span></label>
            <input className="input" value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Service title" />
          </div>
          <div className="field">
            <label>Icon (emoji)</label>
            <input className="input" value={form.icon} onChange={(e) => set('icon', e.target.value)} placeholder="🎨" />
          </div>
          <div className="field full">
            <label>Short description</label>
            <input className="input" value={form.shortDescription} onChange={(e) => set('shortDescription', e.target.value)} placeholder="One-liner" />
          </div>
          <div className="field full">
            <label>Full / large description</label>
            <textarea className="textarea" style={{ minHeight: 160 }} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Detailed description…" />
          </div>
          <div className="field full">
            <label>Tags</label>
            <TagInput value={form.tags} onChange={(v) => set('tags', v)} />
          </div>
          <div className="field">
            <label>Display order</label>
            <input className="input" type="number" value={form.order} onChange={(e) => set('order', e.target.value)} />
          </div>
          <div className="field flex center gap">
            <button type="button" className={`toggle ${form.featured ? 'on' : ''}`} onClick={() => set('featured', !form.featured)} />
            <label style={{ margin: 0 }}>Featured</label>
          </div>
        </div>
      </div>

      <div className="acard">
        <h3>Cover image</h3>
        <ImageUploader value={form.image} onChange={(v) => set('image', v)} multiple={false} label="Upload cover image" />
      </div>

      <button className="btn btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save Service'}</button>
      <ToastHost />
    </form>
  );
}
